<template>
  <tr
    class=" hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-16 last-child:pr-3 text-sm"
    :class="
      {
        'bg-gray-50 border-gray-100': depth <= 1,
        'bg-gray-200 border-gray-300': depth === 2,
        'bg-gray-300 border-gray-400': depth === 3,
        'bg-gray-400 border-gray-500': depth >= 4
      }
    "
  >
    <td
      class="text-left"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.billNumber') + ': ' + tradeItem.billNumber + ', ' + $t('ProgressClaims.TradeRowItem.accessibility.tradeItemName') + ': ' + tradeItem.name"
    >
      <div
        class="flex items-start space-x-2"
        :style="{ paddingLeft: `${(depth * 20) + 16}px` }"
      >
        <span class="font-semibold text-gray-900">{{ $props.tradeItem.billNumber }}</span>

        <ProvisionalSumBadge
          v-if="!tradeItem.has_children && tradeItem.is_provisional_sum"
        />

        <p class="w-60 text-gray-700">
          {{ tradeItem.name }}
        </p>
      </div>
    </td>

    <td
      class="pr-3"
      :title="showBudgetCommentTooltip ? varianceComment.body : ''"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.budgetAmount') + ': ' + australianCurrency(tradeItem.budget) + (showBudgetCommentTooltip ? ', ' + varianceComment.body : '')"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showBudgetCommentButton"
          type="button"
          class="btn-icon btn-icon-default mr-1 shrink-0"
          :aria-label="varianceComment ? $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editComment') : $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.addComment')"
          @click.stop="editBudgetComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <button
          v-else-if="!!varianceComment?.body"
          type="button"
          class="btn-icon btn-icon-default mr-1 shrink-0"
          @click.stop="showBudgetcomment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          <div v-if="childBudget !== 0">
            <span
              v-if="childBudget - childAdjustedBudget !== 0"
              class="text-gray-600"
            >Works:</span> {{ australianCurrency(childBudget) }}
          </div>

          <div v-if="childBudget - childAdjustedBudget !== 0">
            <span
              v-if="childBudget !== 0"
              class="text-gray-600"
            >Adjustments:</span> {{ australianCurrency(-(childBudget - childAdjustedBudget)) }}
          </div>
        </div>
      </div>
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.currentClaim')"
    >
      {{ tradeItemCost ? australianCurrency(tradeItemCost.current_claim) : '' }}
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.currentCertified')"
    >
      {{ tradeItemCost ? australianCurrency(tradeItemCost.current_certified) : '' }}
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.previousAmount') + ': ' + $tcc(tradeItem.previous(claim))"
    >
      {{ tradeItem.budget > 0 ? $tcc(tradeItem.previous(claim)) : '' }}
    </td>

    <td
      class="bg-green-200/50 pr-3"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.cumulativeAmount') + ': ' + $tcc(tradeItem.previous(claim) + (tradeItemCost?.current_certified || 0))"
    >
      {{ tradeItem.budget > 0 ? $tcc(tradeItem.previous(claim) + (tradeItemCost?.current_certified || 0)) : '' }}
    </td>

    <td
      class="bg-blue-100/50"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.claimPercentage') + ': ' + (!falsey(tradeItemCost?.current_claim) ? `${$tn((tradeItemCost.current_claim / tradeItem.budget) * 100)}%` : '')"
    >
      <span v-if="showPercentages">
        {{ tradeItemCost ? $tp((tradeItemCost.current_claim / tradeItem.budget) * 100) : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.certifiedPercentage')"
    >
      {{ tradeItemCost ? $tp((tradeItemCost.current_certified / tradeItem.budget) * 100) : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.previousPercentage')"
    >
      {{ tradeItem.budget > 0 ? $tp((tradeItem.previous(claim) / tradeItem.budget) * 100) : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.cumulativePercentage')"
    >
      {{ tradeItem.budget > 0 ? $tp(((tradeItem.previous(claim) + (tradeItemCost?.current_certified || 0)) / tradeItem.budget) * 100) : '' }}
    </td>

    <td
      class="group"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :title="hasVariance && variance !== 0 && varianceComment?.body ? varianceComment.body : ''"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.varianceTooltip')"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showCommentButton"
          type="button"
          class="btn-icon group-hover:visible btn-icon-default invisible mr-1 shrink-0"
          aria-label="Edit variance comment"
          @click.stop="editVarianceComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ hasVariance ? $tcc(variance) : '' }}
        </div>
      </div>
    </td>

    <td
      class="pr-3"
      :aria-label="$t('ProgressClaims.TradeRowItem.accessibility.remainingBudget') + ': ' + $tcc(Math.abs(tradeItem.budget - tradeItem.previous(claim) - (tradeItemCost?.current_certified || 0)))"
    >
      {{ tradeItem.budget > 0 ? $tcc(Math.abs(tradeItem.budget - tradeItem.previous(claim) - (tradeItemCost?.current_certified || 0))) : '' }}
    </td>
  </tr>
</template>

<script setup lang="ts">
import ProvisionalSumBadge from '@/components/shared/ProvisionalSumBadge.vue';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, falsey } from '@/helpers';
import { t } from '@/i18n';
import Claim, { ClaimStatus } from '@/models/Claim';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useTradeItemCostsStore } from '@/store/tradeItemCosts';
import Swal from 'sweetalert2';
import { computed } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  trade: Trade;
  claim: Claim;
  tradeItem: TradeItem;
  depth?: number;
  showPercentages?: boolean;
}>();

const tradeItemCostsStore = useTradeItemCostsStore();
const updateCommentAction = useStoreApiAction(tradeItemCostsStore.updateComment);
const updateTradeItemCostAction = useStoreApiAction(tradeItemCostsStore.updateTradeItemCost);

const canEditCertified = computed(() => {
  return props.claim.status === ClaimStatus.draft;
});

const tradeItemCost = computed(() => {
  return tradeItemCostsStore.models.with('comments')
    .where('trade_item_id', props.tradeItem.id)
    .where('claim_id', props.claim.id)
    .first();
});

const childBudget = computed(() => {
  return props.tradeItem.childBudget();
});

const childAdjustedBudget = computed(() => {
  return props.tradeItem.childAdjustedBudget();
});

const varianceComment = computed(() => {
  return tradeItemCost.value?.comments[0];
});

const hasVariance = computed(() => {
  return !falsey(tradeItemCost.value?.current_claim) && !falsey(tradeItemCost.value?.current_certified);
});

const variance = computed(() => {
  return tradeItemCost.value?.current_claim - tradeItemCost.value?.current_certified;
});

const showCommentButton = computed(() => {
  return hasVariance.value && variance.value !== 0 && !!varianceComment.value && canEditCertified.value;
});

const showBudgetCommentButton = computed(() => {
  return canEditCertified.value && !falsey(tradeItemCost.value) &&
    !falsey(tradeItemCost.value.current_certified) &&
    !falsey(tradeItemCost.value.current_claim);
});

const showBudgetCommentTooltip = computed(() => {
  return !!varianceComment.value?.body && showBudgetCommentButton.value;
});

const showBudgetcomment = () => {
  Swal.fire({
    icon: 'info',
    title: (hasVariance.value && variance.value !== 0) ? 'Reason for Adjustment' : 'Comment',
    text: varianceComment.value?.body,
  });
};

const editBudgetComment = () => {
  Swal.fire({
    icon: 'info',
    title: (hasVariance.value && variance.value !== 0) ? 'Reason for Adjustment' : 'Comment',
    text: (hasVariance.value && variance.value !== 0) ?
      'Please provide a reason for the variance between the claim and certified amounts.' :
      'Provide an optional comment for the cost.',

    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: varianceComment.value ? 'Update' : 'Save',
  }).then((result) => {
    if(result.isConfirmed && result.value) {
      if(varianceComment.value) {
        updateCommentAction.request(tradeItemCost.value.id, varianceComment.value.id, { comment: result.value }).then(
          () => {
            toast.success('Comment updated');
          },
        );
      } else {
        updateTradeItemCostAction.request(tradeItemCost.value.id, { comment: result.value }).then(
          () => {
            toast.success('Comment saved');
          },
        );
      }
    }
  });
};

const editVarianceComment = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentTitle'),
    text: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentText'),
    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: 'Update',
  }).then((result) => {
    if(result.isConfirmed) {
      if(result.value) {
        updateCommentAction.request(tradeItemCost.value.id, varianceComment.value.id, { comment: result.value }).then(
          () => {
            toast.success('Comment updated');
          },
        );
      }
    }
  });
};
</script>

<style scoped></style>
